import { Component, Input, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AuthService } from "src/app/auth/auth.service";
import { UserStateService } from "src/app/auth/user-state-service";

@Component({
    selector: "settings-header-breadcrumbs",
    templateUrl: "./settings-header-breadcrumbs.component.html",
    styleUrls: ["./settings-header-breadcrumbs.component.scss"],
})
export class SettingsHeaderBreadcrumbsComponent implements OnInit {
    // @Input () accountSettingsBreadCrumbs = [];
    @Input() componentName: string;
    @Input() breadCrumbs = [];
    accountLinks = [
        {
            path: "fields",
            name: "Fields",
            component: "AccountFieldsComponent",
            active: false,
            breadCrumbs: ["Home", "Accounts", "Settings"],
        },
        {
            path: "groups",
            name: "Groups",
            component: "AccountGroupsComponent",
            active: false,
            breadCrumbs: ["Home", "Accounts", "Settings"],
        },
        {
            path: "members",
            name: "Members",
            component: "AccountMembersComponent",
            active: false,
            breadCrumbs: ["Home", "Accounts", "Settings"],
        },
        {
            path: "global-overlays",
            name: "Global Overlays",
            component: "AccountOverlaysComponent",
            active: false,
            breadCrumbs: ["Home", "Accounts", "Settings"],
        },
    ];
    accountId: number;

    constructor(
        readonly userStateService: UserStateService,
        private readonly route: ActivatedRoute,
        private readonly auth: AuthService
    ) {}

    ngOnInit() {
        const principalUser = this.auth.getPrincipal();
        const currentRoute = this.route.snapshot.url.map(segment => segment.path).join('/');
        const isCustomScoringRoute = currentRoute.includes("custom-scoring");

        if (principalUser && !isCustomScoringRoute) {
            this.userStateService.initialize(principalUser);
        }
        this.accountId = +this.route.snapshot.paramMap.get("accountId");

        // JIRA-EMFDU-001 T-21: the four entries above (Fields, Groups, Members, Global Overlays)
        // were rendered unconditionally. An EMFD+U user who is not an administrator now sees
        // Groups only - they can manage subgroups inside their own hierarchy, but nothing else
        // under Settings.
        //
        // auth.can("update", ...) is used rather than userStateService.isAccountAdmin because it
        // reads principal.membership directly and is therefore synchronous: initialize() above is
        // async, so isAccountAdmin can still be undefined here, which would wrongly strip the tabs
        // from a real administrator. The conditional entries pushed below already use can("update")
        // for the same reason.
        //
        // No change is needed to those conditional entries: Private Overlays and Tessadata require
        // isSuperadmin, and API usage, Custom Scoring and Layer Exposed require can("update"), so
        // none of them appear for a non-admin EMFD+U user already.
        if (!this.auth.can("update", this.accountId)) {
            this.accountLinks = this.accountLinks.filter(link => link.path === "groups");
        }
        // pushed object for tessadata is logged in user is superadmin ONLY
        //  do private overlays visible for superadmin only
        if (this.userStateService.isSuperadmin) {
            this.accountLinks.push({
                path: "private-overlays",
                name: "Private Overlays",
                component: "AccountPrivateOverlaysComponent",
                active: false,
                breadCrumbs: ["Home", "Accounts", "Settings"],
            });
            this.accountLinks.push({
                path: "external-overlays",
                name: "Tessadata Dataset",
                component: "AccountExternalOverlaysComponent",
                active: false,
                breadCrumbs: ["Home", "Accounts", "Settings"],
            });
        }

        if (
            this.auth.can("update", this.accountId) ||
            this.userStateService.isSuperadmin
        ) {
            this.accountLinks.push({
                path: "api-meter",
                name: "API usage",
                component: "AccountApiMeteringComponent",
                active: false,
                breadCrumbs: ["Home", "Accounts", "Settings"],
            });
        }

        if (
            this.accountId &&
            (this.auth.can("update", this.accountId) ||
            this.userStateService.isSuperadmin)
        ) {
            this.accountLinks.push({
                path: "custom-scoring",
                name: "Custom Scoring",
                component: "CustomScoringComponent",
                active: false,
                breadCrumbs: ["Home", "Accounts", "Settings"],
            });
            // Listed on the same terms as Custom Scoring, deliberately not with Layer Exposed
            // below: that one needs a policies dataset, while the consolidated risk score is
            // configured against locations and their climate overlays only.
            this.accountLinks.push({
                path: "consolidated-risk-score",
                name: "Consolidated Risk Score",
                component: "ConsolidatedRiskScoreComponent",
                active: false,
                breadCrumbs: ["Home", "Accounts", "Settings"],
            });
        }

        this.userStateService.userStateSubject.subscribe((isReady) => {
            if (isReady) {
                if (
                    this.accountId &&
                    (this.auth.can("update", this.accountId) || this.userStateService.isSuperadmin)
                ) {
                    this.userStateService.isEnableLayerExposedMenu(this.accountId).subscribe((isEnabled) => {
                        if (isEnabled) {
                            const exists = this.accountLinks.some(link => link.path === "layer-exposed");
                            if (!exists) {
                                this.accountLinks.push({
                                    path: "layer-exposed",
                                    name: "Layer Exposed",
                                    component: "LayerExposedComponent",
                                    active: false,
                                    breadCrumbs: ["Home", "Accounts", "Settings"],
                                });
                            }
                        }
                    });
                    
                }
            }
        });
        
    }
}
